import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// ⌘K Command Palette — quick search across Properties, Contacts, Companies,
// and Deals, plus static "Go to" navigation shortcuts.
// Uses ONLY existing endpoints:
//   GET /master-properties?limit=&offset=&search=   (same raw fetch DataHub.tsx uses)
//   api.listContacts / api.listCompanies / api.listCrmDeals
// ─────────────────────────────────────────────────────────────────────────────

interface PaletteItem {
  key: string;
  group: GroupName;
  title: string;
  subtitle?: string;
  to: string;
}

type GroupName = 'Properties' | 'Contacts' | 'Companies' | 'Deals' | 'Go to';

const GROUP_ORDER: GroupName[] = ['Properties', 'Contacts', 'Companies', 'Deals', 'Go to'];

const NAV_SHORTCUTS: Array<{ label: string; to: string }> = [
  { label: 'Prospecting Map', to: '/prospecting-map' },
  { label: 'Property Database', to: '/data-hub' },
  { label: 'Contacts', to: '/crm/contacts' },
  { label: 'Deals', to: '/crm/deals' },
  { label: 'Opportunities', to: '/crm/opportunities' },
  { label: 'Conversations', to: '/crm/conversations' },
  { label: 'Reports', to: '/reports' },
];

const MAX_PER_GROUP = 5;
const DEBOUNCE_MS = 250;

const formatMoney = (value: unknown): string | null => {
  const n = typeof value === 'string' ? parseFloat(value) : (value as number);
  if (typeof n !== 'number' || !isFinite(n) || n <= 0) return null;
  return '$' + Math.round(n).toLocaleString();
};

/** Mirror of the DataHub.tsx master-properties list fetch (no api.ts method exists for it). */
async function searchMasterProperties(search: string): Promise<any[]> {
  const token = localStorage.getItem('token');
  if (!token) return [];
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const params = new URLSearchParams({
    limit: String(MAX_PER_GROUP),
    offset: '0',
    search,
  });
  const response = await fetch(`${apiBase}/master-properties?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch properties');
  return data.properties || [];
}

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [remoteItems, setRemoteItems] = useState<PaletteItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset all transient state whenever the palette closes.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setRemoteItems([]);
      setLoading(false);
      setSelectedIndex(0);
      requestIdRef.current += 1; // invalidate any in-flight search
    }
  }, [open]);

  // Focus the input on open.
  useEffect(() => {
    if (open) {
      // Delay one tick so the element exists after mount.
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const runSearch = useCallback(async (q: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    const [propsRes, contactsRes, companiesRes, dealsRes] = await Promise.allSettled([
      searchMasterProperties(q),
      api.listContacts({ search: q, limit: String(MAX_PER_GROUP), offset: '0' }),
      api.listCompanies({ search: q }),
      api.listCrmDeals({ search: q }),
    ]);

    // A newer keystroke superseded this request — drop the results.
    if (requestId !== requestIdRef.current) return;

    const items: PaletteItem[] = [];

    if (propsRes.status === 'fulfilled') {
      for (const p of propsRes.value.slice(0, MAX_PER_GROUP)) {
        items.push({
          key: `prop-${p.id}`,
          group: 'Properties',
          title: p.address || p.property_name || 'Unnamed property',
          subtitle: [p.city, p.property_type].filter(Boolean).join(' · ') || undefined,
          to: `/data-hub/${p.id}`,
        });
      }
    }

    if (contactsRes.status === 'fulfilled') {
      const contacts: any[] = contactsRes.value.contacts || [];
      for (const c of contacts.slice(0, MAX_PER_GROUP)) {
        const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
        items.push({
          key: `contact-${c.id}`,
          group: 'Contacts',
          title: name || c.email || 'Unnamed contact',
          subtitle: [c.title, c.email].filter(Boolean).join(' · ') || undefined,
          to: `/crm/contacts/${c.id}`,
        });
      }
    }

    if (companiesRes.status === 'fulfilled') {
      const companies: any[] = companiesRes.value.companies || [];
      for (const co of companies.slice(0, MAX_PER_GROUP)) {
        items.push({
          key: `company-${co.id}`,
          group: 'Companies',
          title: co.name || 'Unnamed company',
          subtitle: [co.company_type, co.city].filter(Boolean).join(' · ') || undefined,
          to: `/crm/companies/${co.id}`,
        });
      }
    }

    if (dealsRes.status === 'fulfilled') {
      const deals: any[] = dealsRes.value.deals || [];
      for (const d of deals.slice(0, MAX_PER_GROUP)) {
        items.push({
          key: `deal-${d.id}`,
          group: 'Deals',
          title: d.deal_name || 'Unnamed deal',
          subtitle: [d.stage?.replace(/_/g, ' '), formatMoney(d.deal_value)].filter(Boolean).join(' · ') || undefined,
          to: `/crm/deals/${d.id}`,
        });
      }
    }

    setRemoteItems(items);
    setLoading(false);
  }, []);

  // Debounced search on query change.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setRemoteItems([]);
      setLoading(false);
      requestIdRef.current += 1;
      return;
    }
    const t = window.setTimeout(() => runSearch(q), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, open, runSearch]);

  // "Go to" shortcuts: all of them when idle, substring-filtered when typing.
  const navItems: PaletteItem[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NAV_SHORTCUTS.filter((s) => !q || s.label.toLowerCase().includes(q)).map((s) => ({
      key: `nav-${s.to}`,
      group: 'Go to' as GroupName,
      title: s.label,
      to: s.to,
    }));
  }, [query]);

  // Flat, ordered list used for keyboard selection.
  const flatItems: PaletteItem[] = useMemo(() => {
    const all = [...remoteItems, ...navItems];
    const ordered: PaletteItem[] = [];
    for (const group of GROUP_ORDER) {
      ordered.push(...all.filter((i) => i.group === group));
    }
    return ordered;
  }, [remoteItems, navItems]);

  // Keep the selection in range when results change.
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatItems.length, query]);

  const selectItem = useCallback(
    (item: PaletteItem) => {
      onClose();
      navigate(item.to);
    },
    [navigate, onClose]
  );

  // Keyboard handling while open: arrows, enter, escape.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (flatItems.length ? (i + 1) % flatItems.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (flatItems.length ? (i - 1 + flatItems.length) % flatItems.length : 0));
      } else if (e.key === 'Enter') {
        const item = flatItems[selectedIndex];
        if (item) {
          e.preventDefault();
          selectItem(item);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, flatItems, selectedIndex, onClose, selectItem]);

  if (!open) return null;

  const trimmedQuery = query.trim();

  // Group the flat list back into labelled sections, preserving flat indices
  // so keyboard selection lines up with rendered rows.
  let runningIndex = -1;

  return (
    <div className="fixed inset-0 z-[3000] flex items-start justify-center px-4 pt-[12vh]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 border-b border-gray-100">
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search properties, contacts, companies, deals…"
            autoFocus
            className="w-full py-3.5 text-sm text-gray-900 placeholder-gray-400 bg-transparent border-0 focus:outline-none focus:ring-0"
          />
          {loading && (
            <svg className="w-4 h-4 text-gray-300 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto py-1">
          {GROUP_ORDER.map((group) => {
            const groupItems = flatItems.filter((i) => i.group === group);
            if (groupItems.length === 0) return null;
            return (
              <div key={group}>
                <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {group}
                </p>
                {groupItems.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const selected = index === selectedIndex;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      ref={(el) => {
                        if (selected && el) el.scrollIntoView({ block: 'nearest' });
                      }}
                      className={`w-full flex items-baseline gap-2 px-4 py-2 text-left text-sm transition-colors ${
                        selected ? 'bg-primary-50 text-primary-500' : 'text-gray-700'
                      }`}
                    >
                      <span className="truncate font-medium">{item.title}</span>
                      {item.subtitle && (
                        <span className={`truncate text-xs ${selected ? 'text-primary-500/70' : 'text-gray-400'}`}>
                          {item.subtitle}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {flatItems.length === 0 && !loading && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">
              {trimmedQuery ? `No results for "${trimmedQuery}"` : 'Type to search'}
            </p>
          )}
          {flatItems.length === 0 && loading && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Searching…</p>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">
          ↑↓ navigate · ↵ open · esc close
        </div>
      </div>
    </div>
  );
}
